import React, { useState, useRef, useEffect, useCallback } from "react";
import { Stage, Layer, Image as KonvaImage, Circle, Rect, Text, Star, Ellipse } from "react-konva";
import "bootstrap/dist/css/bootstrap.min.css";

const LedSignDesigner = () => {
  const [konvaImage, setKonvaImage] = useState(null);
  const [shape, setShape] = useState("circle");
  const [text, setText] = useState("Kendin Tasarla!");
  const [glowColor, setGlowColor] = useState("yellow");
  const [imagePosition, setImagePosition] = useState({ x: 150, y: 100 });
  const [textPosition, setTextPosition] = useState({ x: 0, y: 0 });
  const [fontFamily, setFontFamily] = useState("Arial");
  const [fontSize, setFontSize] = useState(30);
  const [imageSize, setImageSize] = useState(0.6);
  const [imageAspectRatio, setImageAspectRatio] = useState(1); // Store logo aspect ratio
  const [dimensions, setDimensions] = useState({ 
    width: 500, 
    height: 500 
  });
  const stageRef = useRef(null);
  const textRef = useRef(null);

  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth < 768) {
        setDimensions({ width: 300, height: 300 });
        setFontSize(20);
        setImageSize(0.4); // Smaller default size on mobile
      } else {
        setDimensions({ width: 500, height: 500 });
        setFontSize(30);
        setImageSize(0.6);
      }
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const calculateInitialTextPosition = useCallback(() => {
    const textWidth = text.length * (fontSize * 0.6);
    if (shape === "circle") {
      setTextPosition({
        x: (dimensions.width - textWidth) / 2,
        y: dimensions.height / 2 + fontSize / 2,
      });
    } else {
      setTextPosition({
        x: (dimensions.width - textWidth) / 2,
        y: dimensions.height * 0.85 - fontSize,
      });
    }
  }, [shape, dimensions, text, fontSize]);

  useEffect(() => {
    calculateInitialTextPosition();
  }, [calculateInitialTextPosition]);

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = () => {
        const img = new window.Image();
        img.src = reader.result;
        img.onload = () => {
          const aspectRatio = img.width / img.height;
          setImageAspectRatio(aspectRatio);
          setKonvaImage(img);
          // Center the image based on shape
          const imgWidth = dimensions.width * imageSize;
          const imgHeight = imgWidth / aspectRatio;
          setImagePosition({
            x: (dimensions.width - imgWidth) / 2,
            y: (dimensions.height - imgHeight) / 2,
          });
        };
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveDesign = () => {
    const stage = stageRef.current.getStage();
    stage.toDataURL({
      callback: (dataUrl) => {
        const link = document.createElement("a");
        link.download = "led-sign-design.png";
        link.href = dataUrl;
        link.click();
      },
    });
  };

  const renderShape = () => {
    const commonProps = {
      shadowBlur: 15,
      shadowColor: glowColor,
      fill: "black",
    };

    switch (shape) {
      case "circle":
        return (
          <Circle
            x={dimensions.width / 2}
            y={dimensions.height / 2}
            radius={dimensions.width / 2.5}
            {...commonProps}
          />
        );
      case "square":
        return (
          <Rect
            x={dimensions.width * 0.1}
            y={dimensions.height * 0.1}
            width={dimensions.width * 0.8}
            height={dimensions.height * 0.8}
            {...commonProps}
          />
        );
      case "star":
        return (
          <Star
            x={dimensions.width / 2}
            y={dimensions.height / 2}
            numPoints={5}
            innerRadius={dimensions.width / 5}
            outerRadius={dimensions.width / 2.5}
            {...commonProps}
          />
        );
      case "ellipse":
        return (
          <Ellipse
            x={dimensions.width / 2}
            y={dimensions.height / 2}
            radiusX={dimensions.width / 2.5}
            radiusY={dimensions.width / 3.5}
            {...commonProps}
          />
        );
      default:
        return null;
    }
  };

  return (
    <div className="container mt-4">
      <h1 className="title text-center mb-4">
        Yadi Baskı ile Kendi Led Tabelanı Tasarla
        <h6>Logonun tabloda daha şık durabilmesi için arkaplanı temiz olmalıdır. remove.bg sitesinden arkaplanı ücretsiz temizleyebilirsiniz.</h6>
      </h1>
      
      <div className="row flex-column flex-md-row">
        {/* Canvas - Mobile'de yukarıda */}
        <div className="col-12 col-md-8 order-1 order-md-2 mb-3 mb-md-0">
          <div className="d-flex justify-content-center align-items-start">
            <div className="border p-2 bg-dark rounded">
              <Stage 
                width={dimensions.width} 
                height={dimensions.height} 
                ref={stageRef}
              >
                <Layer>
                  {renderShape()}
                  {konvaImage && (
                    <KonvaImage 
                      image={konvaImage} 
                      x={imagePosition.x} 
                      y={imagePosition.y} 
                      width={dimensions.width * imageSize} 
                      height={(dimensions.width * imageSize) / imageAspectRatio}
                      shadowBlur={15}
                      shadowColor={glowColor}
                      draggable
                      onDragEnd={(e) => {
                        setImagePosition({
                          x: e.target.x(),
                          y: e.target.y()
                        });
                      }}
                    />
                  )}
                  <Text 
                    ref={textRef}
                    text={text} 
                    x={textPosition.x} 
                    y={textPosition.y} 
                    fontSize={fontSize} 
                    fontFamily={fontFamily} 
                    fill="white" 
                    shadowBlur={10} 
                    shadowColor={glowColor}
                    align="center"
                    width={dimensions.width * 0.8}
                    draggable
                    onDragEnd={(e) => {
                      setTextPosition({
                        x: e.target.x(),
                        y: e.target.y()
                      });
                    }}
                  />
                </Layer>
              </Stage>
            </div>
          </div>
        </div>

        {/* Ayarlar Menüsü */}
        <div className="col-12 col-md-4 order-2 order-md-1">
          <div className="card p-3 h-100">
            <h5>Ayarlar</h5>
            <div className="mb-2">
              <label className="form-label">Logo Yükle</label>
              <input 
                type="file" 
                className="form-control" 
                accept="image/*" 
                onChange={handleImageUpload} 
                style={{ display: 'none' }} 
                id="logo-upload" 
              />
              <label 
                htmlFor="logo-upload" 
                className="btn btn-outline-secondary w-100"
              >
                Logo Yükle
              </label>
            </div>
            <div className="mb-2">
              <label className="form-label">Logo Boyutu: {Math.round(imageSize * 100)}%</label>
              <input
                type="range"
                className="form-range"
                min="0.2"
                max="1"
                step="0.1"
                value={imageSize}
                onChange={(e) => setImageSize(parseFloat(e.target.value))}
              />
            </div>
            <div className="mb-2">
              <label className="form-label">Şekil Seç</label>
              <select 
                className="form-select" 
                value={shape} 
                onChange={(e) => setShape(e.target.value)}
              >
                <option value="circle">Daire</option>
                <option value="square">Kare</option>
                <option value="star">Yıldız</option>
                <option value="ellipse">Elips</option>
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label">Glow Rengi</label>
              <select 
                className="form-select" 
                value={glowColor} 
                onChange={(e) => setGlowColor(e.target.value)}
              >
                <option value="yellow">Sarı</option>
                <option value="red">Kırmızı</option>
                <option value="blue">Mavi</option>
                <option value="green">Yeşil</option>
                <option value="pink">Pembe</option>
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label">Yazı Tipi</label>
              <select 
                className="form-select" 
                value={fontFamily} 
                onChange={(e) => setFontFamily(e.target.value)}
              >
                <option value="Arial">Arial</option>
                <option value="Poppins">Poppins</option>
                <option value="Roboto">Roboto</option>
                <option value="Montserrat">Montserrat</option>
                <option value="Open Sans">Open Sans</option>
              </select>
            </div>
            <div className="mb-2">
              <label className="form-label">Yazı Boyutu: {fontSize}px</label>
              <input
                type="range"
                className="form-range"
                min="10"
                max="50"
                value={fontSize}
                onChange={(e) => setFontSize(parseInt(e.target.value))}
              />
            </div>
            <div className="mb-2">
              <label className="form-label">Metin</label>
              <input 
                type="text" 
                className="form-control" 
                value={text} 
                onChange={(e) => setText(e.target.value.toUpperCase())} 
              />
            </div>
            <button 
              className="btn btn-primary w-100 mt-2" 
              onClick={handleSaveDesign}
            >
              Tasarımı Kaydet
            </button>
            <div className="mt-3 text-center">
              <p>Tasarımına karar verdiğiniz ürünün fiyatını öğrenmek için WhatsApp üzerinden bizimle iletişime geçebilirsiniz.</p>
              <a 
                href="https://wa.me/+905524543679" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="btn btn-success w-100"
              >
                WHATSAPP İLE İLETİŞİME GEÇ
              </a>
            </div>
          </div>
        </div>
      </div>
      
      <link href="https://fonts.googleapis.com/css2?family=Poppins:wght@600&family=Roboto&family=Montserrat&family=Open+Sans&display=swap" rel="stylesheet" />
      <style>
        {`
          .title {
            font-weight: 600;
            font-size: 40px;
            line-height: 47px;
            font-family: 'Poppins', sans-serif;
            background: -webkit-linear-gradient(45deg, #fb00fe, #46b8fe 80%);
            background-clip: text;
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            width: fit-content;
            margin: 0 auto;
          }
          
          @media (max-width: 768px) {
            .title {
              font-size: 28px;
              line-height: 34px;
            }
            .card {
              padding: 15px;
            }
            .form-control, .form-select, .btn {
              font-size: 0.9rem;
            }
            .form-label {
              font-size: 0.85rem;
            }
          }
          
          @media (max-width: 576px) {
            .title {
              font-size: 24px;
              line-height: 30px;
            }
          }

          /* Powered by Section */
          .powered-by-section {
            text-align: center;
            margin-top: 20px;
            padding: 10px 0;
            font-family: 'Roboto', sans-serif;
            font-size: 14px;
            color: #666;
          }

          .powered-by-section p {
            margin: 0;
          }

          .powered-by-section a {
            color: #8b5cf6;
            text-decoration: none;
            font-weight: 600;
            transition: color 0.3s ease;
          }

          .powered-by-section a:hover {
            color: #6b21a8;
            text-decoration: underline;
          }
        `}
      </style>
      <div className="powered-by-section">
        <p>
          Powered by <a href="https://hacialikara.com" target="_blank" rel="noopener noreferrer">H.Ali.K.</a>
        </p>
      </div>
    </div>
  );
};

export default LedSignDesigner;